import React from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

export interface Faq {
  question: string;
  answer: string;
  extra?: React.ReactNode;
}

export const faqs: Faq[] = [
  {
    question: 'What is vapour blasting?',
    answer:
      'Vapour blasting (also called wet blasting or aqua blasting) is a gentle surface restoration process that uses a slurry of water and fine abrasive media propelled by compressed air. The water cushions the media so it cleans and restores surfaces without the warping, pitting or aggressive material removal you can get from traditional dry blasting.',
  },
  {
    question: 'How much does vapour blasting cost?',
    answer:
      'Every job is different — pricing depends on the size, condition and number of parts, plus any masking or disassembly required. Contact us for pricing and we will provide a free, no-obligation quote.',
  },
  {
    question: 'Do you service areas outside Christchurch?',
    answer:
      'Yes. We are based in Islington, Christchurch and regularly take on work from across Canterbury including Hornby, Riccarton, Sockburn, Addington, Rolleston, Rangiora, Ashburton and Timaru. Customers from further afield are welcome to ship parts to us.',
  },
  {
    question: 'What kinds of parts can you vapour blast?',
    answer:
      'Engine cases, cylinder heads, carburettors, alloy wheels, marine fittings, propellers, classic car components and a wide range of industrial parts. The process is especially well-suited to alloy, brass, stainless steel and other delicate materials.',
  },
  {
    question: 'Will vapour blasting damage my parts?',
    answer:
      'No. Because the media is suspended in water, the process is far gentler than dry blasting. It removes corrosion, paint, carbon and contaminants while preserving original surfaces, threads and machined faces.',
  },
  {
    question: 'How long does a typical job take?',
    answer:
      'Most small-to-medium jobs are turned around within a few working days. Larger projects or items needing extensive masking may take longer. We will give you a realistic timeframe when you request a quote.',
  },
  {
    question: 'Do I need to disassemble my parts before bringing them in?',
    answer:
      'Where possible, yes — clean, disassembled parts give the best results and the fastest turnaround. If you are not sure, get in touch and we can advise what to remove or mask before drop-off.',
  },
  {
    question: 'After care — how do I protect my parts once they come back?',
    answer:
      'Freshly vapour blasted alloy is bare and will start to oxidise within days if left untreated. For working parts (engine cases, carbs, fork legs) we recommend ACF-50 corrosion inhibitor. For show parts or anything you want to keep looking factory-fresh long term, a clear coating (2K clear, Eastwood Diamond Clear or Cerakote) or Renaissance Wax works well. Always handle blasted parts with gloves — fingerprints will etch into the surface.',
    extra: (
      <Link
        to="/blog/how-to-care-for-vapour-blasted-parts"
        className="inline-block mt-3 text-primary hover:text-primary/80 font-medium underline underline-offset-4"
      >
        Read the full aftercare guide →
      </Link>
    ),
  },
];

const FaqSection = () => {
  return (
    <section id="faq" className="section-padding bg-background relative">
      <div className="container-wide mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <p className="text-primary font-medium tracking-[0.3em] uppercase mb-4 text-sm">
            Questions
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-6 text-foreground">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Everything you need to know about vapour blasting in Christchurch and Canterbury.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full glass-card rounded-xl px-6">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-border">
              <AccordionTrigger className="text-left text-base md:text-lg font-medium text-foreground hover:text-primary hover:no-underline">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                {faq.answer}
                {faq.extra}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FaqSection;
